import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { BookOpen } from 'lucide-react'
import SEO from '@/components/common/SEO'
import { HowToJsonLd } from '@/components/common/JsonLd'
import SubpageHero from '@/components/common/SubpageHero'
import PageContainer from '@/components/layout/PageContainer'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import { ROUTES } from '@/lib/constants'
import PromotionSlot from '@/components/common/PromotionSlot'

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
}

const GRADES = [
  {
    grade: '1등급',
    score: '95점 이상',
    description: '심신의 기능 상태 장애로 일상생활에서 전적으로 다른 사람의 도움이 필요한 상태',
    examples: '스스로 식사, 옷 입기, 세면, 화장실 이용 등 거의 불가능',
    color: 'bg-red-100 text-red-700 border-red-200',
  },
  {
    grade: '2등급',
    score: '75점 이상 95점 미만',
    description: '일상생활에서 상당 부분 다른 사람의 도움이 필요한 상태',
    examples: '혼자서 옷 입기, 세면 등은 가능하나 보행, 화장실 이용 등 도움 필요',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
  },
  {
    grade: '3등급',
    score: '60점 이상 75점 미만',
    description: '일상생활에서 부분적으로 다른 사람의 도움이 필요한 상태',
    examples: '보행 보조기구 사용, 일부 생활 동작에 도움 필요',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  {
    grade: '4등급',
    score: '51점 이상 60점 미만',
    description: '일상생활에서 일정 부분 다른 사람의 도움이 필요한 상태',
    examples: '대부분 자립 가능하나 일부 활동에 간헐적 도움 필요',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  },
  {
    grade: '5등급',
    score: '45점 이상 51점 미만',
    description: '치매 환자로 일상생활에서 일정 부분 다른 사람의 도움이 필요한 상태',
    examples: '치매 진단을 받은 경우에 한하여 인정',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  {
    grade: '인지지원등급',
    score: '45점 미만',
    description: '치매 환자로 장기요양등급에 해당하지는 않으나 인지 기능 저하로 도움이 필요한 상태',
    examples: '주간보호, 인지활동형 방문요양 등 이용 가능',
    color: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  },
]

const STEPS = [
  { step: '1', title: '장기요양인정 신청', desc: '국민건강보험공단 지사 방문 또는 온라인(www.longtermcare.or.kr) 신청' },
  { step: '2', title: '방문조사', desc: '공단 직원이 신청인을 방문하여 52개 항목의 심신상태를 조사' },
  { step: '3', title: '등급판정', desc: '등급판정위원회에서 장기요양인정점수에 따라 등급 결정 (약 30일 소요)' },
  { step: '4', title: '결과 통보', desc: '장기요양인정서와 표준장기요양이용계획서 발송' },
  { step: '5', title: '서비스 이용', desc: '등급에 맞는 요양시설 또는 재가서비스 선택하여 이용 시작' },
]

export default function GuideCareGradePage() {
  const navigate = useNavigate()

  return (
    <>
      <SEO
        title="장기요양등급 신청 방법 | 등급 판정 기준 안내"
        description="장기요양등급 1~5등급 판정 기준과 신청 방법을 안내합니다. 국민건강보험공단 신청부터 방문조사, 등급 판정까지 5단계를 확인하세요."
        url="/guide/care-grade"
      />
      <HowToJsonLd
        name="장기요양등급 신청 방법"
        steps={STEPS.map((s) => ({ name: s.title, text: s.desc }))}
      />
      <SubpageHero title="요양등급 안내" description="장기요양등급 판정 기준과 신청 방법" icon={<BookOpen size={20} className="text-white" />} />
      <PageContainer>
        <motion.div
          className="flex flex-col gap-6"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
        >
          {/* 소개 */}
          <motion.section variants={fadeUp}>
            <h1 className="text-h2 font-bold text-cb-gray-900">장기요양등급이란?</h1>
            <p className="mt-2 text-body text-cb-gray-600 leading-relaxed">
              장기요양등급은 고령이나 노인성 질병 등으로 일상생활을 혼자 수행하기 어려운 분들에게
              신체활동 또는 가사활동을 지원하는 <strong>장기요양보험</strong> 서비스를 받기 위한 등급입니다.
              국민건강보험공단에서 심신 상태를 조사하여 등급을 판정합니다.
            </p>
          </motion.section>

          {/* 등급별 기준 */}
          <motion.section variants={fadeUp}>
            <h2 className="text-h3 font-bold text-cb-gray-900 mb-4">등급별 기준</h2>
            <div className="flex flex-col gap-3">
              {GRADES.map((g) => (
                <Card key={g.grade} className="border-l-4" style={{ borderLeftColor: 'var(--color-cb-primary)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`rounded-lg border px-2.5 py-1 text-body-sm font-bold ${g.color}`}>
                      {g.grade}
                    </span>
                    <span className="text-caption text-cb-gray-500">{g.score}</span>
                  </div>
                  <p className="text-body-sm text-cb-gray-800 font-medium">{g.description}</p>
                  <p className="text-caption text-cb-gray-500 mt-1">{g.examples}</p>
                </Card>
              ))}
            </div>
          </motion.section>

          {/* 신청 절차 */}
          <motion.section variants={fadeUp}>
            <h2 className="text-h3 font-bold text-cb-gray-900 mb-4">신청 절차</h2>
            <div className="flex flex-col gap-3">
              {STEPS.map((s) => (
                <div key={s.step} className="flex gap-3.5">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-cb-primary text-white text-body-sm font-bold">
                    {s.step}
                  </div>
                  <div className="flex-1 pt-0.5">
                    <p className="text-body-sm font-bold text-cb-gray-900">{s.title}</p>
                    <p className="text-caption text-cb-gray-500 mt-0.5">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          {/* 알아두면 좋은 정보 */}
          <motion.section variants={fadeUp}>
            <h2 className="text-h3 font-bold text-cb-gray-900 mb-4">알아두면 좋은 정보</h2>
            <Card style={{ background: 'var(--gradient-card-cool)' }}>
              <ul className="flex flex-col gap-2.5 text-body-sm text-cb-gray-700">
                <li className="flex gap-2"><span className="text-cb-primary font-bold">&#10003;</span> 만 65세 이상이거나 노인성 질병이 있는 분 신청 가능</li>
                <li className="flex gap-2"><span className="text-cb-primary font-bold">&#10003;</span> 등급 판정까지 약 30일 소요</li>
                <li className="flex gap-2"><span className="text-cb-primary font-bold">&#10003;</span> 기초생활수급자는 본인부담금 면제, 그 외 의료급여 수급자·병원비 경감 차상위계층·건강보험료 하위 25% 이하는 60% 감경, 하위 25~50%는 40% 감경</li>
                <li className="flex gap-2"><span className="text-cb-primary font-bold">&#10003;</span> 등급 갱신은 유효기간 만료 90일 전부터 신청 가능</li>
                <li className="flex gap-2"><span className="text-cb-primary font-bold">&#10003;</span> 등급 판정에 이의가 있으면 심사 청구 가능</li>
              </ul>
            </Card>
          </motion.section>

          {/* CTA */}
          <motion.section variants={fadeUp} className="flex flex-col gap-3 pb-4">
            <Button variant="secondary" size="lg" fullWidth onClick={() => navigate(ROUTES.SELF_ASSESSMENT)}>
              내 예상 등급 자가진단하기
            </Button>
            <Button variant="primary" size="lg" fullWidth onClick={() => navigate(ROUTES.RECOMMEND)}>
              내 등급으로 맞춤 추천 받기
            </Button>
            <Button variant="ghost" size="lg" fullWidth onClick={() => navigate(ROUTES.COST_SIMULATOR)}>
              등급별 비용 계산해보기
            </Button>
          </motion.section>

          {/* 프로모션 슬롯: guide_bottom */}
          <PromotionSlot slotKey="guide_bottom" className="mt-2" />
        </motion.div>
      </PageContainer>
    </>
  )
}
